import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { LEGAL_PAGES } from "@/data/legalContent";

export default function LegalPage() {
    const { doc } = useParams();
    const page = LEGAL_PAGES[doc];

    useEffect(() => {
        if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
        window.scrollTo(0, 0);
        if (page) document.title = `Keypoteca — ${page.title}`;
        return () => {
            document.title =
                "Keypoteca — Consigue la hipoteca que necesitas con las mejores condiciones";
        };
    }, [doc, page]);

    if (!page) {
        return (
            <div className="min-h-screen bg-[#112338] flex flex-col items-center justify-center px-6 text-center">
                <h1 className="font-display text-3xl font-bold text-white">
                    Documento no encontrado
                </h1>
                <Link
                    to="/"
                    className="mt-6 inline-flex items-center gap-2 rounded-full bg-emerald-500 text-[#0B1A2B] font-bold px-6 py-3"
                >
                    <ArrowLeft size={17} /> Volver al inicio
                </Link>
            </div>
        );
    }

    return (
        <div data-testid={`legal-page-${doc}`} className="min-h-screen bg-white">
            <header className="relative overflow-hidden bg-[#112338] noise-overlay px-4 sm:px-6 lg:px-8 pt-10 pb-16">
                <div className="absolute inset-0 blueprint-grid" />
                <div className="relative max-w-3xl mx-auto">
                    <div className="flex items-center justify-between">
                        <Link to="/" aria-label="Volver al inicio">
                            <img
                                src="/assets/logo-white-trim.png"
                                alt="Keypoteca"
                                className="h-8 w-auto"
                            />
                        </Link>
                        <Link
                            data-testid="legal-back"
                            to="/"
                            className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors"
                        >
                            <ArrowLeft size={16} /> Volver
                        </Link>
                    </div>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="mt-12 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-white"
                    >
                        {page.title}
                    </motion.h1>
                    <p className="mt-3 font-mono text-xs uppercase tracking-[0.2em] text-emerald-400">
                        {page.updated}
                    </p>
                </div>
            </header>

            <main className="px-4 sm:px-6 lg:px-8 py-14">
                <div className="max-w-3xl mx-auto space-y-10">
                    {page.sections.map((s, i) => (
                        <motion.section
                            key={i}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-40px" }}
                            transition={{ duration: 0.5 }}
                        >
                            <h2 className="font-display text-xl font-bold text-[#0F172A]">
                                {s.heading}
                            </h2>
                            {s.paragraphs && (
                                <div className="mt-3 space-y-3 text-[15px] text-slate-600 leading-relaxed">
                                    {s.paragraphs.map((p, pi) => (
                                        <p key={pi}>{p}</p>
                                    ))}
                                </div>
                            )}
                            {s.list && (
                                <ul className="mt-4 space-y-2.5">
                                    {s.list.map((item, li) => (
                                        <li
                                            key={li}
                                            className="flex items-start gap-3 text-[15px] text-slate-600 leading-relaxed"
                                        >
                                            <CheckCircle2
                                                size={17}
                                                className="text-emerald-500 shrink-0 mt-0.5"
                                            />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {s.closing && (
                                <p className="mt-3 text-[15px] text-slate-600 leading-relaxed">
                                    {s.closing}
                                </p>
                            )}
                        </motion.section>
                    ))}

                    <div className="pt-6 border-t border-slate-100">
                        <p className="text-sm text-slate-500">
                            ¿Dudas sobre este documento? Escríbenos a{" "}
                            <a
                                href="mailto:hola@keypoteca.es"
                                className="text-emerald-600 font-semibold hover:text-emerald-700"
                            >
                                hola@keypoteca.es
                            </a>{" "}
                            o llama al{" "}
                            <a
                                href="tel:+34622344472"
                                className="text-emerald-600 font-semibold hover:text-emerald-700"
                            >
                                +34 622 344 472
                            </a>
                            .
                        </p>
                    </div>
                </div>
            </main>

            <footer className="border-t border-slate-100 py-8 px-4 text-center">
                <p className="text-xs text-slate-400">
                    © 2026 Keypoteca. Todos los derechos reservados.
                </p>
            </footer>
        </div>
    );
}
